import type { SdkWallet } from '@morfi/backend-sdk-v2/client';

export const MULTI_WALLET_OPERATIONS = [
  'create',
  'import',
  'external_connect',
  'external_reconnect',
  'external_disconnect',
  'switch',
  'secondary_rename',
  'secondary_export',
  'archive',
  'restore',
  'move_funds',
] as const;

export type MultiWalletOperation = (typeof MULTI_WALLET_OPERATIONS)[number];

export const MULTI_WALLET_SURFACES = [
  'header',
  'wallet_management',
  'settings',
  'portfolio',
  'deposit_withdraw',
  'faucet',
  'onboarding',
  'proposal_funding',
  'gift_send',
  'gift_claim',
  'indirect_transaction',
] as const;

export type MultiWalletSurface = (typeof MULTI_WALLET_SURFACES)[number];
export type MultiWalletGateState = 'pending' | 'off' | 'on';

export interface MultiWalletScope {
  readonly gateState: MultiWalletGateState;
  readonly enabled: boolean;
  readonly primaryWallet: SdkWallet | null;
  readonly wallets: readonly SdkWallet[];
  readonly archivedWallets: readonly SdkWallet[];
  readonly activeWalletId: string | null;
}

interface ResolveMultiWalletScopeInput {
  readonly gateState: MultiWalletGateState;
  readonly wallets: readonly SdkWallet[];
  readonly archivedWallets: readonly SdkWallet[];
  readonly activeWalletId: string | null;
}

interface ThesisAttribution {
  readonly trader?: { readonly walletAddress?: string | null } | null;
}

interface ActivityAttribution {
  readonly trader?: string | null;
  readonly participationId?: string | null;
}

export function deriveMultiWalletGateState(
  value: boolean | null | undefined | string | number,
): MultiWalletGateState {
  if (value === true) return 'on';
  if (value === null || value === undefined) return 'pending';
  return 'off';
}

export function canStartMultiWalletOperation(state: MultiWalletGateState): boolean {
  return state === 'on';
}

export function resolvePrimaryEmbeddedWallet(wallets: readonly SdkWallet[]): SdkWallet | null {
  const primaryWallets = wallets.filter(
    (wallet) => wallet.type === 'embedded' && wallet.isPrimary && wallet.archivedAt === null,
  );
  return primaryWallets.length === 1 ? (primaryWallets[0] ?? null) : null;
}

export function resolveMultiWalletScope({
  gateState,
  wallets,
  archivedWallets,
  activeWalletId,
}: ResolveMultiWalletScopeInput): MultiWalletScope {
  const primaryWallet = resolvePrimaryEmbeddedWallet(wallets);
  if (gateState === 'on') {
    return {
      gateState,
      enabled: true,
      primaryWallet,
      wallets,
      archivedWallets,
      activeWalletId,
    };
  }

  return {
    gateState,
    enabled: false,
    primaryWallet,
    wallets: primaryWallet === null ? [] : [primaryWallet],
    archivedWallets: [],
    activeWalletId: primaryWallet?.isAvailable ? primaryWallet.id : null,
  };
}

export function isThesisAttributedToWallet(
  thesis: ThesisAttribution,
  walletAddress: string | null,
): boolean {
  if (walletAddress === null) return false;
  return thesis.trader?.walletAddress === walletAddress;
}

export function isActivityAttributedToWallet(
  activity: ActivityAttribution,
  walletAddress: string | null,
): boolean {
  if (walletAddress === null) return false;
  if (activity.trader === walletAddress) return true;
  const segments = activity.participationId?.split(':');
  if (segments === undefined || segments.length !== 3) return false;
  return segments[2] === walletAddress;
}
